<template>
  <aside
    :aria-label="t('sidebar.navigation')"
    :class="[
      'fixed inset-y-0 start-0 z-20 flex h-dvh min-w-[100px] flex-col border-e border-gray-200 bg-form-box',
      'overflow-x-hidden overflow-y-auto print:hidden',
      'motion-safe:transition-[width,translate,transform,visibility] motion-safe:duration-normal motion-safe:ease-out',
      collapsed ? 'w-[210px] lg:w-[100px]' : 'w-[210px] lg:w-[210px] 2xl:w-[300px]',
      isToggleSideBar || !isMobile
        ? 'visible translate-x-0'
        : 'invisible ltr:-translate-x-full rtl:translate-x-full',
      'lg:visible lg:translate-x-0',
    ]"
  >
    <!-- logo -->
    <div class="flex shrink-0 items-center justify-center px-4 py-5">
      <img :src="APP_LOGO" :alt="APP_TITLE" width="64" height="64" class="max-w-16" fetchpriority="high" />
    </div>

    <!-- menu -->
    <div class="w-full flex-1 px-2">
      <ul class="space-y-0.5">
        <SideBarGroup
          v-for="group in groups"
          :key="group.id"
          :group="group"
          :open="openGroups.includes(group.id)"
          :collapsed="isMobile ? false : collapsed"
          :active-item-path="activeItemPath"
          @toggle="handleGroupToggle"
          @navigate="closeMobileDrawer"
        />
      </ul>
    </div>

    <!-- collapse toggle (desktop only) -->
    <div class="hidden shrink-0 border-t border-gray-200 p-2 lg:block">
      <button
        type="button"
        :aria-expanded="!collapsed"
        :aria-label="t(collapsed ? 'sidebar.expand' : 'sidebar.collapse')"
        :title="t(collapsed ? 'sidebar.expand' : 'sidebar.collapse')"
        class="flex h-10 w-full cursor-pointer items-center gap-2.5 rounded-lg px-3 text-start text-body whitespace-nowrap text-font-gray hover:bg-bg-input hover:text-font-dark motion-safe:transition-colors motion-safe:duration-fast focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-font-gray focus-visible:ring-offset-2 focus-visible:ring-offset-form-box"
        :class="collapsed ? 'lg:justify-center' : ''"
        @click="toggleCollapsed"
      >
        <Icon
          :icon="collapsed ? 'tabler:chevrons-right' : 'tabler:chevrons-left'"
          width="22"
          height="22"
          class="shrink-0 rtl:rotate-180"
        />
        <span class="select-none" :class="collapsed ? 'lg:hidden' : ''">
          {{ t(collapsed ? 'sidebar.expand' : 'sidebar.collapse') }}
        </span>
      </button>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useEventListener } from '@vueuse/core'
import { Icon } from '@iconify/vue'
import SideBarGroup from '@/components/layouts/SideBarGroup.vue'
import { useSidebarMenu } from '@/composables/useSidebarMenu'

// props --------------------
const props = defineProps<{
  isMobile: boolean
  isToggleSideBar: boolean
}>()

// emits --------------------
const emits = defineEmits<{
  (e: 'toggleSideBar', value: boolean): void
}>()

const { t } = useI18n()
const { groups, openGroups, activeItemPath, collapsed, toggleCollapsed, handleGroupToggle } =
  useSidebarMenu('main')

const { APP_LOGO, APP_TITLE } = CONFIG

// Close the mobile drawer after navigating to a menu item
const closeMobileDrawer = () => {
  if (props.isMobile) emits('toggleSideBar', false)
}

// ESC closes the mobile drawer
useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.isMobile && props.isToggleSideBar) {
    emits('toggleSideBar', false)
  }
})
</script>
